import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * The optimistic swap to the empty state, once this component has played one.
   * Left in place after it settles: a resolved promise is the right answer for
   * every later read, which is "nothing to wait for".
   * @type {Promise<unknown> | null}
   */
  #emptyingCart = null;

  /**
   * Acknowledges a stepper press as it happens rather than when the debounced
   * request finally goes out. 300ms of coalescing on top of the round trip is
   * long enough that a tap on +/- reads as a control that did nothing, so the
   * shimmer `updateQuantity` would start later starts here instead — same
   * attribute, cleared on the same path (`resetShimmer`).
   *
   * @param {Event} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    this.refs.cartTotal?.shimmer();

    if (line) {
      const row = this.refs.cartItemRows?.[line - 1];
      /** @type {TextComponent | null | undefined} */ (row?.querySelector('text-component'))?.shimmer();
    }

    // Stepping to 0 removes the line, and there is no following press to
    // coalesce it with - holding it in the debounce only buys 300ms of a row
    // that sits there looking untouched before it starts collapsing. Every
    // other change keeps the debounce, so a held +/- still sends one request.
    if (quantity === 0) {
      this.#debouncedOnChange.cancel();
      this.#onQuantityChange(event);
      return;
    }

    this.#debouncedOnChange(event);
  };

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      this.#emptyingCart = startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends. `subtree: false` because the
      // only animation being waited on is the row's own collapse: stepping the
      // last unit off a line shimmers its total, and that animation is
      // infinite, so a subtree wait never resolves on its own - it sat there
      // until the response happened to clear the shimmer.
      onAnimationEnd(row, remove, { subtree: false });
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    // Assigned once the response has decided what the morph is. A removal
    // defers its morph behind the row's collapse, and the cart is not settled -
    // nor safe to click a second removal in - until that has landed, so the
    // re-enable below waits on this rather than on the request alone.
    /** @type {Promise<unknown> | null} */
    let pendingMorph = null;

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        // The shimmer is what says a figure is being worked out. A removal
        // holds its morph back until the row has finished collapsing, so
        // clearing it here would park the pre-removal totals, un-shimmered, on
        // screen for the length of that animation; it is cleared with the morph
        // instead. Everything else lands straight away.
        if (config.action !== 'clear' || parsedResponseText.errors) resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        const applyMorph = async () => {
          const recommendations = this.#recommendationsSignature();

          await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          });

          this.#fadeInRecommendations(recommendations);
        };

        // A removal is animated optimistically the moment it is clicked (see
        // `onLineItemRemove`), and this morph would cut that short: rows are
        // keyed by their id, so the row the shopper is watching collapse has no
        // counterpart in the response and is deleted outright — the animation
        // stops half-played and everything below it snaps up. Hold the morph
        // until the collapse has finished and it has nothing left to do to the
        // row.
        //
        // What is left is the second wave a removal always causes: the count,
        // the totals, and the recommendation strip (the removed product's
        // suggestions leave the merge). Each of those is animated where it
        // stands — `#fadeInRecommendations` above and `#flashUpdated` below.
        //
        // This used to be a bare `startViewTransition(applyMorph)` on the cart
        // page. A view transition with no named elements captures the WHOLE
        // document, so the browser froze the page, rasterised it, and
        // cross-faded every pixel of it — header, item list, summary rail and
        // all — to animate a total and a row of suggestions. By this point the
        // list has already finished moving (the collapse above), so the one
        // thing that crossfade was guaranteed to do was repaint a list that was
        // not changing, a third of a second after the click. That is what the
        // review is describing.
        const morphed =
          config.action === 'clear' ? this.#removalSettled().then(applyMorph) : applyMorph();

        // One catch for the whole chain: a morph that throws must still clear
        // the shimmer and give the cart back, and `pendingMorph` is awaited
        // below, so it must not reject on its own.
        pendingMorph = morphed.catch((error) => {
          console.warn('[cart-items] Cart section morph failed:', error);
        });

        pendingMorph.then(() => {
          resetShimmer(this);
          this.#updateCartQuantitySelectorButtonStates();

          // A removal has no line left to flash - its row collapsed out of the
          // list - but the cart total it just moved is the same figure a
          // quantity change flashes, and it now carries the change on its own:
          // nothing else marks the moment since the page-wide crossfade went.
          if (config.action !== 'change') {
            this.#flashUpdated([this.refs.cartTotal]);
            return;
          }

          // Queried off the document rather than through `refs`: the morph has
          // just rewritten the rows and the refs proxy catches up on its own
          // schedule.
          const price = lineId && this.querySelector(`[data-key="${CSS.escape(lineId)}"] .cart-items__price`);

          this.#flashUpdated([price || null, this.refs.cartTotal]);
        });
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        // `finally` on the morph rather than `then`: a rejected morph must
        // still give the cart back. A null one (the request failed, or the
        // response carried errors) re-enables on the spot.
        Promise.resolve(pendingMorph).finally(() => this.#enableCartItems());
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    // An add from the page opens the drawer straight away, long before the
    // response that re-renders it - so the recommendations it opens on are the
    // ones computed for the cart as it was. Hold them back until the morph has
    // swapped in the strip for the new cart (see `#holdRecommendations`).
    const holdRecommendations = Boolean(event.promise) && this.isDrawer && this.#isExternalCartUpdate(event);
    if (holdRecommendations) this.#holdRecommendations();

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);
          // What each line was worth going in, so the lines this update
          // actually moved can be told apart from the ones it left alone.
          const previousTotals = new Map(
            (this.refs.cartItemRows ?? []).map((row) => [
              row.dataset.key,
              row.querySelector('.cart-items__price')?.textContent?.trim(),
            ])
          );

          const recommendations = this.#recommendationsSignature();

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            if (holdRecommendations) this.#releaseRecommendations();
            this.#fadeInRecommendations(recommendations, holdRecommendations);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            /** @type {Element[]} */
            const changed = [];
            /** @type {HTMLElement[]} */
            const added = [];

            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                added.push(row);
                continue;
              }

              // Adding a product already in the cart lands on an existing row,
              // where the only thing that moves is a figure part-way down the
              // panel. Without this the drawer opens on a list that looks
              // exactly as it was left.
              const price = row.querySelector('.cart-items__price');
              if (price && price.textContent?.trim() !== previousTotals.get(row.dataset.key)) changed.push(price);
            }

            this.#expandAddedRows(added);
            this.#flashUpdated([...changed, this.refs.cartTotal]);
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      })
      // Every other way out - a failed add, a response without sections, a
      // morph that threw - shows whatever the drawer now holds rather than
      // leaving the strip hidden for good.
      .finally(() => {
        if (holdRecommendations) this.#releaseRecommendations();
      });
  };

  /**
   * Disables the cart items.
   *
   * `cart-items-disabled` kills pointer events on the whole component, the
   * checkout button included, for the length of the request. `aria-busy` says
   * the same thing to assistive tech, and the class now has a look of its own
   * (see cart-products.liquid) so a click that does nothing reads as "busy"
   * rather than "broken".
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Resolves once the optimistic removal this component started has finished
   * playing — the rows' collapse, or the swap to the empty state.
   *
   * Read back off the DOM rather than handed down from `onLineItemRemove`: the
   * request goes out before the animation is set up, and more than one removal
   * can be in flight, so a field holding "the" pending collapse would be a
   * race. Whatever is still collapsing when the response lands is what this
   * waits for.
   *
   * @returns {Promise<unknown>}
   */
  #removalSettled() {
    const collapsing = this.querySelectorAll('.cart-items__table-row.removing');

    // Each row is already being awaited by `onLineItemRemove`, which removes it
    // on the same event; a second waiter on the same animation is free.
    // `subtree: false` for the same reason it uses it - see there.
    if (collapsing.length) {
      return Promise.allSettled(
        Array.from(collapsing, (row) => onAnimationEnd(row, () => {}, { subtree: false }))
      );
    }

    return this.#emptyingCart ?? Promise.resolve();
  }

  /**
   * The products each recommendation strip in this component is showing, as one
   * string per strip.
   *
   * The strips are computed from the cart itself (snippets/cart-recommended-products),
   * so a line joining or leaving it can change every card in the row: the
   * removed product's cross-sells drop out of the merge, the added one's join
   * it, and whatever is in the cart is filtered out of both. The morph swaps
   * those cards in place, which is the one part of a cart update that really
   * does repaint a whole list.
   *
   * Keyed by the element, which the morph updates rather than replaces, so the
   * before and after of the same strip can be compared.
   *
   * @returns {Map<Element, string>}
   */
  #recommendationsSignature() {
    /** @type {Map<Element, string>} */
    const signatures = new Map();

    for (const strip of this.querySelectorAll('.product-selection')) {
      const links = strip.querySelectorAll('.product-selection__card a[href]');

      signatures.set(strip, Array.from(links, (link) => link.getAttribute('href')).join());
    }

    return signatures;
  }

  /**
   * Fades a recommendation strip back in when a cart update has changed what it
   * is showing.
   *
   * Only when the cards actually moved on: most quantity changes leave the
   * merge exactly as it was, and the morph leaves those strips untouched (it
   * skips subtrees that compare equal), so animating unconditionally would put
   * a fade on a row that did not change.
   *
   * @param {Map<Element, string>} before - The signatures taken before the morph.
   * @param {boolean} [always] - Fade every strip, changed or not: they were held
   *   back while the update was in flight, so each one is appearing, not changing.
   */
  #fadeInRecommendations(before, always = false) {
    if (prefersReducedMotion()) return;

    for (const [strip, signature] of this.#recommendationsSignature()) {
      if (!always && (!before.has(strip) || before.get(strip) === signature)) continue;

      // A held strip had its heading hidden along with the cards, so the two
      // arrive together; otherwise the heading never left and only the cards fade.
      const parts = always ? ['panel', 'intro'] : ['panel'];

      for (const part of parts) {
        const element = strip.querySelector(`.product-selection__${part}`);
        if (!(element instanceof HTMLElement)) continue;

        const refreshed = `product-selection__${part}--refreshed`;

        // Restart rather than extend, the same way `#flashUpdated` does.
        element.classList.remove(refreshed);
        void element.offsetWidth;
        element.classList.add(refreshed);

        // `subtree: false`: the cards carry scroll-driven entry animations that
        // never finish on a clock, and a card still shimmering would hold this
        // open indefinitely.
        onAnimationEnd(element, () => element.classList.remove(refreshed), { subtree: false });
      }
    }
  }

  /**
   * Hides the drawer's recommendation cards until the cart update in flight
   * has re-rendered them.
   *
   * The strip is computed from the cart, so until the response lands it shows
   * the cross-sells of the cart before the add - and, on a drawer opened for
   * the first time, the deferred pane may well be fetched before the add has
   * reached the cart, with the same result. Either way the shopper would watch
   * one set of cards replaced by another. The heading is held with them, so
   * the strip fades in as one.
   *
   * On this element rather than the pane, which may not exist yet (it is
   * deferred until the drawer's first open) or may be replaced by the morph.
   */
  #holdRecommendations() {
    this.toggleAttribute('data-recommendations-pending', true);
  }

  #releaseRecommendations() {
    this.removeAttribute('data-recommendations-pending');
  }

  /**
   * Opens the rows a cart update has just added, from nothing to their own
   * height.
   *
   * The height is measured here rather than left to the stylesheet because
   * `height: auto` is not an animatable end state outside Chrome (see the
   * `addRow` keyframes in snippets/cart-products.liquid) - the row would jump
   * to full size half way through instead of making room for itself, which is
   * the whole point of the animation. `offsetHeight`, not `clientHeight`: under
   * the theme's `border-box` that is what `height` means, borders included, so
   * a row with a divider closes on its own edge rather than a pixel short.
   *
   * Every row is measured before any of them is stamped: `.adding` collapses
   * the row it is put on, and a later measurement taken across that reads a
   * list mid-move.
   *
   * @param {HTMLElement[]} rows - The rows the morph added.
   */
  #expandAddedRows(rows) {
    if (!rows.length) return;

    const heights = rows.map((row) => row.offsetHeight);

    rows.forEach((row, index) => {
      row.style.setProperty('--row-height', `${heights[index]}px`);
      row.classList.add('adding');

      // `subtree: false`: the wait is for the row's own expansion, and a
      // lingering `.adding` would leave the card clipped with its padding
      // zeroed for good.
      onAnimationEnd(
        row,
        () => {
          row.classList.remove('adding');
          row.style.removeProperty('--row-height');
        },
        { subtree: false }
      );
    });
  }

  /**
   * Flashes figures that have just been replaced, once they are in the DOM. It
   * pairs with the shimmer: the shimmer says a number is being worked out, this
   * says it has landed. On a long line a one-step change moves the total by
   * very little and is easy to miss entirely.
   *
   * Reduced motion skips it — the numbers themselves carry everything this
   * adds.
   *
   * @param {(Element | null | undefined)[]} elements - The figures to flash.
   */
  #flashUpdated(elements) {
    if (prefersReducedMotion()) return;

    for (const target of elements) {
      if (!(target instanceof HTMLElement)) continue;

      // Restart rather than extend when two changes land back to back: without
      // the reflow the class is already there and the animation never replays.
      target.classList.remove('cart-value-updated');
      void target.offsetWidth;
      target.classList.add('cart-value-updated');

      onAnimationEnd(target, () => target.classList.remove('cart-value-updated'), { subtree: false });
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
